import { useState } from "react";

import { Tooltip } from "antd";
import { clsx } from "clsx";
import { useHotkeys } from "react-hotkeys-hook";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { RVIcon } from "../../atoms/RVIcon/RVIcon";

import styles from "./RVSidebarItem.module.scss";

export type RVSidebarItemProps = {
    /** Name of the icon to render in the sidebar item. */
    icon: string;
    /** Destination route the item links to. Also used to compute the active state. */
    href: string;
    /** Additional routes that should mark this item as active when matched. */
    activeRoutes?: string[];
    /** Single-key keyboard shortcut (used as `alt+<hotkey>`) that navigates to `href`. */
    hotkey?: string | undefined;
    /** Tooltip content shown on hover, typically the item label. */
    children: JSX.Element | string;
    /** Optional click handler. When provided, the item renders as a button instead of a link. */
    onClick?: () => void;
    /** Whether the item is rendered. Defaults to `true`. */
    visible?: boolean;
};

const checkActive = (href: string, activeRoutes: string[], pathname: string | undefined) => {
    if (!pathname) {
        return false;
    }

    if (pathname?.match(new RegExp(`^${href}($|/)`))) {
        return true;
    }
    if (activeRoutes) {
        for (const route of activeRoutes) {
            if (pathname?.match(new RegExp(`^${route}($|/)`))) {
                return true;
            }
        }
    }
    return false;
};

/**
 * A navigation item for the app sidebar. Renders an icon with a hover tooltip and
 * highlights itself as active when the current route matches `href` or any
 * `activeRoutes`. Supports an optional keyboard shortcut and a click handler
 * (which renders the item as a button rather than a link).
 *
 * @example
 * <RVSidebarItem icon="home" href="/home" hotkey="h">
 *     Home
 * </RVSidebarItem>
 */
export function RVSidebarItem({
    icon,
    href,
    activeRoutes = [],
    children,
    hotkey = undefined,
    onClick,
    visible = true,
}: RVSidebarItemProps): JSX.Element {
    const navigate = useNavigate();
    const location = useLocation();

    const [activeTitle, setActiveTitle] = useState<JSX.Element | string>(children);
    useHotkeys(hotkey ? `alt+${hotkey}` : "", () => {
        if (href) {
            navigate(href);
        }

        if (onClick) {
            onClick();
        }
    });

    if (!visible) {
        return <></>;
    }

    if (onClick) {
        return (
            <div onClick={onClick}>
                <Tooltip placement="right" title={activeTitle} onOpenChange={onTooltipVisibleChange}>
                    <div className={styles.navItem}>
                        <RVIcon icon={icon} />
                    </div>
                </Tooltip>
            </div>
        );
    }

    // we need check if we are on our route or on any that we want to look active for
    const isActive = checkActive(href || "", activeRoutes, location.pathname);

    function onTooltipVisibleChange(visible: boolean): void {
        if (visible) {
            setActiveTitle(children);
            if (hotkey) {
                setTimeout(
                    () =>
                        setActiveTitle(
                            <div translate="no">
                                {children}
                                <div>
                                    <i>
                                        hotkey &lsquo;{/^mac/i.test(window.navigator.platform) ? "Option" : "Alt"} +{" "}
                                        {hotkey}&rsquo;
                                    </i>
                                </div>
                            </div>
                        ),
                    3000
                );
            }
        }
    }

    return (
        <Link to={href}>
            <Tooltip placement="right" title={activeTitle} onOpenChange={onTooltipVisibleChange}>
                <div
                    className={clsx(styles.navItem, {
                        [styles.active]: isActive,
                    })}
                >
                    <RVIcon icon={icon} />
                </div>
            </Tooltip>
        </Link>
    );
}
